// @ts-strict-ignore
import { ProjectHelper } from '../../../helper/project.helper';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { IOperation } from '@shared/undo/operation/i-operation';
import { ArticleLocationVO } from '@article/model/vo/article-location.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleUtils } from '@article/util/article-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsagePlausiPropertyDef } from '@article/constants/article-usage-plausi-property-def';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { IArticleInsertProcessor } from './prepare/insert/i-article-insert.processor';
import { ArticleInsertProcessorRegistry } from './prepare/insert/article-insert-processor-registry';
import { ArticleContainersWrapper } from '@3d-scene/model3d/article-containers-wrapper';
import { ArticleManager } from '@article/manager/article.manager';
import { ProjectModel } from '@project/model/project.model';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ReplaceBusbarArticleHelper } from '@article/helper/replace/busbar/replace-busbar-article.helper';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { lastValueFrom } from 'rxjs';
import { Log } from '@shared/debug/log';
import { MountingRailCarrierType, MountingRailCreationModel } from '@configuration/wizard/service/mounting-rail-creation.model';
import { AddArticleError, AttachArticleError } from './add-article-error';

export class BaseArticleLocationProcessor<T extends ArticleLocationVO> {

  private readonly INITIAL_ROTATION_ANGLE: number = 180;

  protected _insertProcessor: IArticleInsertProcessor;

  protected _articleContainer: ArticleContainer;
  protected _acceptingConnectionArea: ConnectionAreaVO;

  protected _articlesWrapper: ArticleContainersWrapper;
  protected _givingConnectionAreas: ConnectionAreaVO[];

  protected _insertArticleHelper: InsertArticleHelper;

  protected _additionalUndoOperations: IOperation[] = [];

  constructor(
    protected projectModel: ProjectModel,
    protected articleManager: ArticleManager,
    protected insertProcessorRegistry: ArticleInsertProcessorRegistry,
    protected projectHelper: ProjectHelper,
    protected localeResourceManager: LocaleResourceManager,
    protected _articleLocation: T,
    protected _article: ArticleVO,
    protected connectionInfoService: ConnectionInfoService,
    protected replaceBusbarHelper: ReplaceBusbarArticleHelper,
    private _insertArticleHelperFactory: InsertArticleHelperFactory
  ) { }

  protected initInsertArticleHelper(): void {
    this._insertArticleHelper = this._insertArticleHelperFactory.createInstanceByAcceptingContainer(this._articleContainer, this._acceptingConnectionArea.name);
  }

  protected async loadFullArticle(): Promise<void> {

    try {
      let article = this.articleManager.getCachedArticle(this._article.identity, this._article.version);
      if (!article) {
        article = await lastValueFrom(this.articleManager.newLoadArticle_handler(this._article.identity, this._article.version));
      }
      this._article = article;
    } catch (error) {
      Log.error(error);
      const errorMessage = await this.processArticleFailure(`Could not load article.`);
      throw new AddArticleError(errorMessage);
    }
  }

  private processingFullArticle(): void {
    this._insertProcessor = this.insertProcessorRegistry.get(this._article);
    const articleToConnect: ArticleVO = !this._article.baseArticle ? this._article : this._article.baseArticle;
    let requiredConnections: ConnectionAreaVO[];
    if (this._articleLocation.checkConnectionProperties) {
      requiredConnections = ConnectionInfoUtils.findAllRequiredConnections(articleToConnect, this._articleContainer.article,
        this._acceptingConnectionArea);
    } else {
      requiredConnections = ArticleUtils.getConnectionElements(articleToConnect, ConnectionAreaVO.SIDE_GIVING);
    }

    if (!requiredConnections || requiredConnections.length  === 0) {
      const errorMessage: string = this.localeResourceManager.getValueByKey('articleLocationProcessor.message.error_required_connection_not_found');
      throw new AttachArticleError(errorMessage, {article: this._articleContainer.article}, {article: articleToConnect});
    }

    this._givingConnectionAreas = requiredConnections;
  }

  protected async loadAndProcessFullArticle(): Promise<void> {
    await this.loadFullArticle();
    this.processingFullArticle();
  }

  protected findAllGivingCubesForConnect(): Connection[] {
    const givingCubes: Connection[] = [];
    let givingCube: Connection;
    for (const givingConnectionArea of this._givingConnectionAreas) {
      givingCube = this._insertProcessor.findGivingCubeByConnectionArea(
        this._articlesWrapper.articleContainers,
        this._articlesWrapper.baseArticleContainer,
        givingConnectionArea
      );
      if (givingCube) {
        givingCubes.push(givingCube);
      }
    }

    return ConnectionInfoUtils.sortConnections(givingCubes, true);
  }

  protected calculateArticleWidth(givingCubes: Connection[]): number {
    const firstGivingRectangle: Rectangle3D = this.connectionInfoService.calculateRectangleByConnection(givingCubes[0], true);
    const lastGivingRectangle: Rectangle3D = this.connectionInfoService.calculateRectangleByConnection(givingCubes[givingCubes.length - 1], true);
    return firstGivingRectangle.point1.distanceTo(lastGivingRectangle.point2);
  }

  protected async createAndRefreshWrapper(insertProcessor = this._insertProcessor): Promise<void> {
    try {
      this._articlesWrapper = insertProcessor.createArticleContainersWrapper(this._article, this._articleLocation, this._insertArticleHelper);
      await this._articlesWrapper.refreshWrapper();
    } catch (error) {
      await this.processFailure(`Could not create the article wrapper.`);
      throw new AddArticleError(error.message)
    }
  }

  protected getRotateAngleOfBaseContainer(baseContainer: ArticleContainer, rotationAngle: number): number {
    if (!baseContainer) {
      return rotationAngle;
    }
    if (
      NumberUtils.isNotNumber(rotationAngle) &&
      !ArticleUtils.isDrillingHole(baseContainer.article) &&
      // SMAR-385: ignore open side rotation for zone articles
      !ArticleUtils.isZoneArticle(baseContainer.article)
    ) {

      // May be need additional check RotateArticlePresenter.findGivingConnectionsWithAngle with considerConnectionAngles=true
      rotationAngle = this.getOpenSideRotationAngle(this._articleContainer);
    }
    if (NumberUtils.isNotNumber(rotationAngle)) {
      rotationAngle = 0;
    }

    const appliedRotationAngle: number = ArticleUsageUtils.getArticleUsagePlausiPropertyIntValue(
      baseContainer.articleUsage,
      ArticleUsagePlausiPropertyDef.APPLIED_ROTATION_ANGLE
    );
    if (NumberUtils.isNumber(appliedRotationAngle)) {
      rotationAngle += appliedRotationAngle;
    }

    if (this.projectHelper.isInitialRotationRequired(baseContainer)) {
      rotationAngle += this.INITIAL_ROTATION_ANGLE;
    }
    rotationAngle %= 360;
    return rotationAngle;
  }

  private getOpenSideRotationAngle(railContainer: ArticleContainer): number {
    let openSideRotationAngle: number = 0;
    if (
      railContainer &&
      MountingRailCreationModel.LEFT_OPEN_SIDE ===
        ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(railContainer.articleUsage, ArticleUsageInfoPropertyDef.MOUNTING_RAIL_OPEN_SIDE) as MountingRailCarrierType
    ) {
      // set rotation by 180-degrees for article which should be connected to mounting rail with left open side
      openSideRotationAngle = 180;
    }
    return openSideRotationAngle;
  }

  /**
   * Returns the error message and triggers the process failure
   * @returns {string} error message for the load articles error
   */
  protected async processArticleFailure(reason: string): Promise<string> {
    const errorMessage: string = this.localeResourceManager.getValueByKey('articleLocationProcessor.message.error_load_article',
      [this._article.articleNo]);
    await this.processFailure(reason);
    return errorMessage;
  }

  protected async processFailure(reason: string): Promise<void> {
    Log.error(reason);
    await this.revertAdditionalUndoOperations();
    this._articlesWrapper?.disposeArticleContainers();
  }

  private async revertAdditionalUndoOperations(): Promise<void> {
    if (!this._additionalUndoOperations || this._additionalUndoOperations.length === 0) {
      return;
    }
    for (const additionalUndoOperation of this._additionalUndoOperations) {
      await additionalUndoOperation.performUndo();
    }
    this._additionalUndoOperations = [];
  }
}
